import type { Layout } from 'react-grid-layout';

export const DASHBOARD_COLUMNS = 12;

export type WidgetSizePresetId = 'small' | 'medium' | 'large' | 'full';

export const WIDGET_SIZE_PRESET_IDS: WidgetSizePresetId[] = ['small', 'medium', 'large', 'full'];

/** A few fixed sizes the user can pick from (clamped to each widget's min size). */
export const WIDGET_SIZE_PRESETS: Record<
  WidgetSizePresetId,
  { label: string; w: number; h: number }
> = {
  // Thirds-based spans (4 / 6 / 8 / 12 of the 12-col grid) so the designed layout —
  // a 3-across KPI row of 4-wide tiles and full-width rows — maps onto a preset, and
  // the gear menu can highlight a tile's current size.
  small: { label: 'Small', w: 4, h: 3 },
  medium: { label: 'Medium', w: 6, h: 4 },
  large: { label: 'Large', w: 8, h: 5 },
  full: { label: 'Full width', w: 12, h: 6 },
};

/** Applies a size preset to a layout item, respecting min sizes and grid bounds. */
export function applyWidgetSizePreset(item: Layout, sizeId: WidgetSizePresetId): Layout {
  const preset = WIDGET_SIZE_PRESETS[sizeId];
  const w = Math.min(DASHBOARD_COLUMNS, Math.max(preset.w, item.minW ?? 1));
  const h = Math.max(preset.h, item.minH ?? 1);
  return { ...item, w, h, x: Math.min(item.x, Math.max(0, DASHBOARD_COLUMNS - w)) };
}

/** Returns the preset that matches an item's current size, if any (to mark it selected). */
export function getWidgetSizePresetId(item?: Layout): WidgetSizePresetId | undefined {
  if (!item) {
    return undefined;
  }
  return WIDGET_SIZE_PRESET_IDS.find(sizeId => {
    const preset = WIDGET_SIZE_PRESETS[sizeId];
    return (
      item.w === Math.max(preset.w, item.minW ?? 1) && item.h === Math.max(preset.h, item.minH ?? 1)
    );
  });
}
